/**
 * ## ItemEditor.tsx ##
 * This file contains ItemEditor component
 * @packageDocumentation
 */

import React from 'react';
import { useLocation } from 'react-router-dom';

import {
	Form, Formik, FormikProps, FormikConfig, FormikHelpers,
} from 'formik';
import isEqual from 'lodash/isEqual';

import { WithDeleted } from '@common/typescript/objects/WithDeleted';
import Button from '@common/react/components/Forms/Button';
import {
	useItemProviderContext,
	ItemProviderContext,
} from '@common/react/components/Core/ItemProvider/ItemProvider';
import Loader from '@common/react/components/Core/LoaderProvider/Loader';
import SimpleLoader from '@common/react/components/UI/Loaders/BounceLoader';
import FormikRef from '@common/react/components/Core/ItemEditor/FormikRef';
import LeaveConfirmationModal, { LeaveConfirmationModalProps } from '@common/react/components/Modal/LeaveConfirmationModal';
import { useModal } from '@common/react/components/Modal/ModalContextProvider';
import { BaseParams } from '@common/react/objects/BaseParams';
import ScrollToError from '@common/react/components/Forms/ScrollToError';

/**
 * ViewOptions - passed to ItemEditor View callback
 *
 * @interface ViewOptions
 * @template {WithDeleted} T - item type
 * @template TResponse - response type
 */
export interface ViewOptions<T extends WithDeleted, TResponse = T> extends ItemProviderContext<T, TResponse> {
	/**
	 * function changing viewing mode
	 */
	toggleReadonly: () => void;
}

/**
 * EditOptions - passed to ItemEditor Edit callback
 *
 * @interface EditOptions
 * @template {WithDeleted} T - item type
 * @template TResponse - response type
 */
export interface EditOptions<T extends WithDeleted, TResponse = T> extends ViewOptions<T, TResponse> {
	/**
	 * formik data object. FormikProps<T>
	 */
	formikBag: FormikProps<T>;
	/**
	 * flag indicates ItemEditor detected entity changes or not
	 */
	haveChanges: boolean;
}

/**
 * BeforeSubmitOptions - passed to ItemEditor BeforeSubmit callback
 *
 * @interface BeforeSubmitOptions
 * @template {WithDeleted} T - item type
 */
interface BeforeSubmitOptions<T extends WithDeleted> {
	/**
	 * submitted values
	 */
	values: T;
	/**
	 * Formik actions
	 */
	actions: FormikHelpers<T>;
	/**
	 * submit callback
	 */
	submit: () => Promise<void>;
}

/**
 * AfterSubmitOptions - passed to ItemEditor AfterSubmit callback
 *
 * @interface AfterSubmitOptions
 * @template {WithDeleted} T - item type
 * @template TResponse - response type
 */
interface AfterSubmitOptions<T extends WithDeleted, TResponse = T> {
	/**
	 * new item after submit
	 */
	item: T;
	/**
	 * request response
	 */
	res: TResponse;
	/**
	 * submitted values
	 */
	values: T;
}

/**
 * render function for view mode
 *
 * @template {WithDeleted} T - item type
 * @template TResponse - response type
 * @param options - contains ItemProvider context and other props {@link ViewOptions}
 */
export type ItemEditorViewDelegate<T extends WithDeleted, TResponse = T>
	= (options: ViewOptions<T, TResponse>) => React.ReactNode;

/**
 * render function for edit mode
 *
 * @template {WithDeleted} T - item type
 * @template TResponse - response type
 * @param options - contains ItemProvider context and other props {@link EditOptions}
 */
export type ItemEditorEditDelegate<T extends WithDeleted, TResponse = T>
	= (options: EditOptions<T, TResponse>) => React.ReactNode;

/**
 * ItemEditor component props
 *
 * @interface ItemEditorProps
 * @template {WithDeleted} T - item type
 * @template TResponse - response type
 * @template ModalProps - modal props type
 */
interface ItemEditorProps<T extends WithDeleted, TResponse = T, ModalProps = BaseParams> {
	/**
	 * render function in edit mode
	 *
	 * @returns markup for edit mode
	 */
	edit: ItemEditorEditDelegate<T, TResponse>;
	/**
	 * render function in view mode
	 *
	 * @returns markup for view mode
	 */
	view?: ItemEditorViewDelegate<T, TResponse>;
	/**
	 * function that determines the initial value for the form
	 * @param item - item from ItemProvider
	 * @returns data for form
	 */
	getInitialValues?: (item: T) => Partial<T>;
	/**
	 * editor loader
	 * @default <Loader defaultLoader={<SimpleLoader />} type="pageLoader" />
	 */
	loader?: React.ReactNode;
	/**
	 * link to get formikBag outside the form
	 */
	formRef?: React.MutableRefObject<FormikProps<any> | null>;
	/**
	 * formik properties. It is possible to overwrite values such as initialValues, onSubmit, validationSchema
	 */
	formikProps?: Partial<FormikConfig<T>>;
	/**
	 * callback after item is saved
	 * - For example: make some changes at state
	 * @param options - AfterSubmitOptions
	 */
	afterSubmit?: (options: AfterSubmitOptions<T, TResponse>) => void;
	/**
	 * callback before send request
	 * - For example. You can show a modal confirmation before sending the request
	 * @param options - BeforeSubmitOptions
	 */
	beforeSubmit?: (options: BeforeSubmitOptions<T>) => void;
	/**
	 * if true and customButtons are not defined, default buttons will be displayed
	 */
	withButtons?: boolean;
	/**
	 * function to handle cancel button click. If not defined, the cancel button will not be displayed
	 */
	onCancel?: () => void;
	/**
	 * function to show custom buttons
	 * @param item - item from ItemProvider
	 * @param formikBag - formik data object. FormikProps<T>
	 * @param disabled - disabled for save button
	 * @param submit
	 * @returns custom buttons markup
	 */
	customButtons?: (item: T, formikBag: FormikProps<T>, disabled: boolean, submit: () => Promise<any>) => React.ReactNode;
	/**
	 * className for default buttons container
	 */
	defaultButtonsContainerClassName?: string;
	/**
	 * function to get cancel button props
	 */
	getCancelButtonsProps?: (formikBag: FormikProps<T>) =>
		Omit<React.DetailedHTMLProps<React.ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement>, 'type' | 'onClick'>;
	/**
	 * function to get save button props
	 */
	getSaveButtonsProps?: (formikBag: FormikProps<T>) =>
		Omit<React.DetailedHTMLProps<React.ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement>, 'type' | 'onClick'>;
	/**
	 * add some custom buttons near default submit button
	 */
	buttons?: React.ReactNode;
	/**
	 * the time (ms) during which success messages will be displayed
	 * @default 5000
	 */
	showMessageDuration?: number;
	/**
	 * message after formed saved without errors
	 * @default 'Successfully saved'
	 */
	successMessage?: string;
	/**
	 * determines whether the form values need to be reset after saving
	 * @default true
	 */
	resetFormAfterSubmit?: boolean;
	/**
	 * determines whether error or success messages should be shown
	 * @default true
	 */
	showMessages?: boolean;
	/**
	 * readonly mode flag. Default value get from context
	 */
	readonly?: boolean;
	/**
	 * get request name before submit form
	 * @param values - current form values
	 * @returns request name for save
	 */
	getRequestName?: (values: T) => string;
	/**
	 * the save button is disabled if there are no changes to the form
	 * @default false
	 */
	detectChanges?: boolean;
	/**
	 * text at default save button
	 * @default 'Save'
	 */
	saveText?: string;
	/**
	 * LeaveConfirmationModal component props
	 */
	leaveConfirmationModalProps?: Omit<LeaveConfirmationModalProps, 'when' | 'onOk'>;
	/**
	 * custom equality check function
	 * @default lodash.isEqual
	 */
	customEqual?: (initialValues: T, values: T) => boolean;
	/**
	 * custom formProps
	 */
	formProps?: React.FormHTMLAttributes<HTMLFormElement>;
	/**
	 * if not undefined will show confirmation modal before submit
	 */
	confirmMessageProps?: ModalProps;
	/**
	 * scroll to validationMessage
	 * @default true
	 */
	scrollToError?: boolean;
}

const ItemEditorMessage: React.FC<{message: string}> = ({ message }) => {
	const ref = React.useRef<HTMLDivElement>(null);
	React.useEffect(() => {
		if (message) {
			ref.current?.scrollIntoView({ block: 'center', behavior: 'auto' });
		}
	}, [message]);

	return <>
		{message ? <div ref={ref} className="alert alert-success">{message}</div> : null}
	</>;
};

/**
 * ItemEditor component.
 *
 * @example ItemEditor with edit template only:
 * <ItemProvider<T> loadRequest="request">
 *   <ItemEditor<T>
 *     edit={() => {React.ReactNode}}
 *   />
 * </ItemProvider>
 *
 * @template {WithDeleted} T - item type
 * @template TResponse - response type
 * @template ModalProps - modal props type
 * @param props - {@link ItemEditorProps}
 * @type {React.FC<ItemEditorProps>}
 * @returns React.ReactElement
 */
export const ItemEditor: <T extends WithDeleted, TResponse = T, ModalProps = BaseParams>(
	props: ItemEditorProps<T, TResponse, ModalProps>
) => React.ReactElement = <T extends WithDeleted, TResponse = T, ModalProps = BaseParams>(
	props: ItemEditorProps<T, TResponse, ModalProps>,
) => {
	const context = useItemProviderContext<T, TResponse>();

	const {
		state: {
			item,
			loading,
			itemLoading,
			readonly: readonlyContext,
			error,
			validationSchema,
			loadRequest,
			message,
			transformAfterSave,
			resetAfterSave,
		},
		actions: {
			update,
			setMessage,
			setError,
		},
	} = context;

	const readonlyProps = props.readonly;
	const preventAfterSubmit = React.useRef(false);
	const ref = React.useRef<FormikProps<T>>(null as any);
	const location = useLocation();
	const modalContext = useModal();

	const [readonly, setReadonly] = React.useState<boolean>(readonlyProps ?? (item && item.id < 0 ? false : readonlyContext));
	const {
		getInitialValues = (item: T) => item,
		loader = <Loader defaultLoader={<SimpleLoader />} type="pageLoader" />,
		formRef,
		formikProps,
		afterSubmit,
		view = () => {
			return null;
		},
		edit,
		withButtons,
		onCancel,
		customButtons,
		buttons,
		getSaveButtonsProps,
		getCancelButtonsProps,
		defaultButtonsContainerClassName = 'text-center form-group',
		resetFormAfterSubmit = true,
		showMessages = true,
		confirmMessageProps,
		beforeSubmit: defaultBeforeSubmit = (confirmMessageProps ? (({ submit }) => {
			modalContext.openConfirm({
				...confirmMessageProps,
				onOk: () => {
					submit();
				},
			});
		}) : null),
		showMessageDuration = 5000,
		successMessage = 'Successfully saved',
		getRequestName,
		detectChanges,
		saveText = 'Save',
		leaveConfirmationModalProps = {} as Required<ItemEditorProps<T>>['leaveConfirmationModalProps'],
		customEqual = isEqual,
		formProps,
		scrollToError = true,
	} = props;

	const {
		message: leaveModalMessage = 'There is unsaved data on the current page. Save before leaving?',
		handleBlockedNavigation = (nextLocation) => nextLocation.pathname.includes(location.pathname),
		...otherLeaveConfirmationModalProps
	} = leaveConfirmationModalProps;

	React.useEffect(() => {
		const callback = () => setReadonly(readonlyProps ?? readonlyContext);
		const haveChanges = detectChanges ? !ref.current
			|| !customEqual(formikProps?.initialValues ?? ref.current.initialValues, ref.current.values) : false;
		if (haveChanges && ref.current && (readonlyProps ?? readonlyContext) && !readonly) {
			modalContext.openConfirm({
				onCancel: callback,
				onOk: () => {
					preventAfterSubmit.current = false;
					ref.current?.submitForm()
						.then(() => callback());
				},
				content: leaveModalMessage,
				cancelText: otherLeaveConfirmationModalProps.cancelText || 'No',
				okText: otherLeaveConfirmationModalProps.okText || 'Yes',
			});
		} else {
			callback();
		}
	}, [readonlyProps ?? readonlyContext, formikProps?.initialValues]);

	const handleSubmit = (values: T, actions: FormikHelpers<T>, beforeSubmit = defaultBeforeSubmit) => {
		const submit = () => update(values, true, getRequestName ? getRequestName(values) : undefined, preventAfterSubmit.current)
			.then((res) => {
				setMessage(successMessage);
				hideSuccess();
				const newValues = resetAfterSave ? item : {
					...item,
					...transformAfterSave({ item: values, response: res, prevItem: item }),
				};
				if (resetFormAfterSubmit) {
					actions?.resetForm({
						values: { ...item, ...getInitialValues(newValues) },
						submitCount: 0,
					});
				}
				setTimeout(() => {
					!preventAfterSubmit.current && afterSubmit && afterSubmit({ item: newValues, res, values });
					preventAfterSubmit.current = false;
				}, 0);
			});

		return beforeSubmit == null ? submit() : beforeSubmit({ values, actions, submit });
	};

	const toggleReadonly = () => {
		setReadonly((prev) => !prev);
	};

	const hideSuccess = () => {
		setTimeout(() => {
			setMessage('');
		}, showMessageDuration < 500 ? 500 : showMessageDuration);
	};

	if (itemLoading || !item) {
		return <>{loader}</>;
	}

	if (readonly) {
		return <>{view({ ...context, toggleReadonly })}</>;
	}

	return <Formik<T>
		onSubmit={handleSubmit}
		validationSchema={validationSchema}
		{...formikProps}
		initialValues={formikProps?.initialValues === undefined ? { ...item, ...getInitialValues(item) } : formikProps?.initialValues}
		validate={(values) => {
			const obj = new Proxy(values, {
				get: (target, prop) => {
					if (typeof prop === 'string' && !prop.includes('$')) {
						if (target && !(prop in target) && prop !== 'then' && prop !== 'catch') {
							setError(`${prop} property is missing from Item`);
						}
					}
					// eslint-disable-next-line
					// @ts-ignore
					return target[prop];
				},
			});
			return formikProps?.validate?.(obj) || validationSchema?.validate(obj, { abortEarly: false })
				.then(() => undefined)
				.catch((err) => {
					const obj: any = {};
					if (typeof err === 'string') {
						return err;
					}
					Object.keys(err)
						.filter((key) => err[key] !== 'ValidationError')
						.forEach((key) => {
							if (err[key]) obj[key] = err[key];
						});
					return obj;
				});
		}}
	>
		{(formikBag) => {
			const haveChanges = detectChanges ? !customEqual(formikProps?.initialValues ?? formikBag.initialValues, formikBag.values) : false;
			ref.current = formikBag;
			const saveButtonsProps = getSaveButtonsProps?.(formikBag);
			const cancelButtonsProps = getCancelButtonsProps?.(formikBag);
			return <Form id={`${loadRequest}-editor-form`} {...formProps}>
				{formRef !== undefined ? <FormikRef formikRef={formRef} formikBug={formikBag} /> : null}
				{scrollToError && <ScrollToError {...formikBag} formId={formProps?.id ?? `${loadRequest}-editor-form`} />}
				{detectChanges ? <LeaveConfirmationModal
					when={haveChanges}
					message={leaveModalMessage}
					handleBlockedNavigation={handleBlockedNavigation}
					onOk={(leaveLocation) => {
						preventAfterSubmit.current = true;
						formikBag.submitForm()
							?.then(() => {
								leaveLocation();
							});
					}}
					{...otherLeaveConfirmationModalProps}
				/> : null}
				{edit({
					...context,
					formikBag,
					toggleReadonly,
					haveChanges,
				})}
				{customButtons
					? customButtons(
						item,
						formikBag,
						detectChanges ? !haveChanges : false,
						() => formikBag.submitForm(),
					)
					: withButtons && <div className={defaultButtonsContainerClassName}>
						<Button
							disabled={detectChanges ? !haveChanges : saveButtonsProps?.disabled}
							isLoading={loading}
							other={saveButtonsProps}
							className={saveButtonsProps?.className}
							title={saveButtonsProps?.title}
						>
							{saveText}
						</Button>
						{onCancel && <button
							className="btn btn-danger"
							{...cancelButtonsProps}
							type="button"
							onClick={onCancel}
						>
							Cancel
						</button>}
						{buttons && buttons}
					</div>
				}
				{showMessages && <>
					<ItemEditorMessage message={message} />
					{error ? <div className="alert alert-danger">{error}</div> : ''}
				</>}
			</Form>;
		}}
	</Formik>;
};
